/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import NotFound from "components/NotFound";
import MenuBar from "./MenuBar";
import { ModulesProvider } from "./ModulesContext";
import AppContext from "AppContext";
import List from "./List";
import PersonalInfo from "./PersonalInfo";
import Accounting from "./Accounting";
import ProgressInfo from "./ProgressInfo";
import Reports from "./Reports";
import Attachments from "./Attachments";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import { useDispatch, useSelector } from "react-redux";
import { getForm, getDataNormalizer, getListHead, getFiltersBuilder } from "../Forms";
import Workflow from "./Workflow";
import { getAdvancedFiltersContext } from "./List/AdvancedFiltersContexts";
import Chat from "./Chat";
import Header from "./../../common_components/Header";
import ClientInfoView from "../Forms/ClientInfoView";

function Modules({ match, dbName, formName, code, name }) {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [fileCases, setFileCases] = useState(undefined);
    const [currentFileCase, setCurrentFileCase] = useState(undefined);
    const [resources, setResources] = useState({});
    const [accountingData, setAccountingData] = useState(undefined);
    const [balance, setBalance] = useState(undefined);
    const [progressInfoData, setProgressInfoData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageableData, setPageableData] = useState(undefined);
    const [pageableAccountingData, setPageableAccountingData] = useState(undefined);
    const [pageableProgressInfoData, setPageableProgressInfoData] = useState(undefined);
    const [clientPhoto, setClientPhoto] = useState(undefined);
    const [attachments, setAttachments] = useState(undefined);
    const [workflow, setWorkflow] = useState(undefined);
    const [advancedFilters, setAdvancedFilters] = useState(undefined);
    const AdvancedFiltersContext = getAdvancedFiltersContext(formName);

    const defaultRequestData = {
        filter: {},
        orderBy: "id",
        orderDirection: "desc",
        pageNumber: 0,
        pageSize: 5,
    };

    const {
        api: {
            Modules,
            Management: { Customers },
            General: { Log, Storage },
            Workflows: { Workflows },
        },
        module,
    } = useContext(AppContext);

    const DB = Modules(dbName);

    function getFileCases(requestData = defaultRequestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.PersonalInfo.getList({
            onSuccess: (data, pageableData) => {
                setFileCases(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
            normalizeData: getDataNormalizer(formName),
        });
    }

    async function getFileCasesAsync(requestData = defaultRequestData) {
        return DB.PersonalInfo.getListAsync(requestData, getDataNormalizer(formName));
    }

    useEffect(() => {
        const { search } = location;
        if (search) {
            try {
                const queryParams = new URLSearchParams(search);
                const fileCaseId = queryParams.get("fileCaseId");
                const openTab = queryParams.get("openTab");
                const loadEntity = async () => {
                    setIsLoading(true);
                    const res = await DB.PersonalInfo.getOneByIdAsync(fileCaseId, getDataNormalizer(`${formName}_Single`));
                    setCurrentFileCase(res);
                    if (openTab) {
                        goToTab(openTab);
                    } else {
                        goToDetailHandler();
                    }
                };

                loadEntity();
            } catch (error) {
                dispatch(notifyError(error));
            } finally {
                setIsLoading(false);
            }
        } else {
            getFileCases();
        }

        return () => {
            setFileCases([]);
        };
    }, []);

    function getResources() {
        DB.PersonalInfo.getResources({
            moduleCode: module.code,
            onSuccess: (data) => {
                setResources(data);
            },
            onError: (error) => {
                setResources({});
                dispatch(notifyError(error));
            },
        });
    }

    async function getBalanceAsync() {
        return DB.Accounting.getBalanceAsync(currentFileCase.id);
    }

    async function getBalance() {
        try {
            const newBalance = await getBalanceAsync();
            setBalance(newBalance);
        } catch (error) {
            setBalance(0);
        }
    }

    function getAccounting(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.Accounting.getList({
            personalInfoId: currentFileCase.id,
            onSuccess: async (data, pageableData) => {
                await getBalance();
                setAccountingData(data);
                setPageableAccountingData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                setPageableAccountingData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    function getProgressInfo(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.ProgressInfo.getList({
            personalInfoId: currentFileCase.id,
            onSuccess: (data, pageableData) => {
                setProgressInfoData(data);
                setPageableProgressInfoData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                setPageableProgressInfoData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    async function getClientPhotoAsync(clientId) {
        try {
            const photoURL = await Storage.getDownloadURLAsync({
                dirName: "management/clients/images",
                fileName: `client_id_${clientId || currentFileCase.client.id}`,
            });
            setClientPhoto(photoURL);
        } catch (message) {}
    }

    async function getAttachmentsAsync() {
        try {
            const _attachments = await DB.Attachments.getAllByFileCaseIdAsync(currentFileCase.id);
            setAttachments(_attachments);
        } catch (message) {}
    }

    async function getWorkflowAsync(newTrnFlowId) {
        if (newTrnFlowId || currentFileCase.trnFlowId) {
            try {
                setIsLoading(true);
                const _workflow = await Workflows.getOneByIdAsync({
                    id: newTrnFlowId || currentFileCase.trnFlowId,
                });
                setWorkflow(_workflow);
            } catch (message) {
            } finally {
                setIsLoading(false);
            }
        } else {
            setWorkflow(undefined);
        }
    }

    async function restartWorkflowAsync() {
        if (currentFileCase.trnFlowId) {
            try {
                setIsLoading(true);
                const res = await Workflows.restart({
                    id: currentFileCase.trnFlowId,
                });
                if (res) {
                    dispatch(notifySuccess("Workflow restarted successfully!"));
                    await getWorkflowAsync();
                }
            } catch (error) {
                const customError = error && error.response && error.response.data && error.response.data.message;
                dispatch(notifyError(customError || error));
            } finally {
                setIsLoading(false);
            }
        }
    }

    async function startWorkflowAsync() {
        try {
            setIsLoading(true);
            const response = await DB.PersonalInfo.startNewWorkflow(currentFileCase.id);
            const newTrnFlowId = response && response.data;
            if (newTrnFlowId) {
                dispatch(notifySuccess("Workflow started successfully!"));
                setCurrentFileCase({
                    ...currentFileCase,
                    trnFlowId: newTrnFlowId,
                });
                await getWorkflowAsync(newTrnFlowId);
            }
        } catch (error) {
            const customError = error && error.response && error.response.data && error.response.data.message;
            dispatch(notifyError(customError || error));
        } finally {
            setIsLoading(false);
        }
    }

    async function startConversationAsync(userId) {
        try {
            setIsLoading(true);
            const response = await DB.PersonalInfo.startNewConversation(currentFileCase.id, userId);
            const newModuleConversation = response && response.data;
            if (newModuleConversation) {
                dispatch(notifySuccess("Conversation started successfully!"));
                setCurrentFileCase((prev) => ({ ...prev, client: { ...prev.client, conversations: [newModuleConversation] } }));
            }
        } catch (error) {
            const customError = error && error.response && error.response.data && error.response.data.message;
            dispatch(notifyError(customError || error));
        } finally {
            setIsLoading(false);
        }
    }

    function restartData() {
        setAccountingData(undefined);
        setProgressInfoData(undefined);
        setBalance(undefined);
        setClientPhoto(undefined);
        setWorkflow(undefined);
    }

    useEffect(() => {
        if (currentFileCase) {
            setClientPhoto(undefined);
            getAccounting();
            getProgressInfo();
            getAttachmentsAsync();
            getClientPhotoAsync();
            getWorkflowAsync();
        } else {
            restartData();
        }
    }, [currentFileCase]);

    useEffect(() => {
        if (module) {
            getResources();
        }
    }, [module]);

    const header = currentFileCase && (
        <ClientInfoView
            client={currentFileCase.client}
            balance={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }).format(balance || 0)}
        />
    );

    function redirect(path) {
        history.push(`${match.path}${path}`);
    }

    function goToDetailHandler() {
        redirect("/personal-info/detail");
    }

    function goToTab(tabName) {
        redirect(`/${tabName}`);
    }

    function goToAddHandler() {
        restartData();
        redirect("/personal-info/add");
    }

    function goToUpdateHandler() {
        redirect("/personal-info/update");
    }

    function cancelHandler() {
        redirect("/list");
    }

    return (
        <ModulesProvider
            value={{
                ...DB,
                Customers,
                personalInfoData: fileCases,
                getFileCases,
                getFileCasesAsync,
                currentFileCase,
                clientPhoto,
                getClientPhotoAsync,
                setCurrentFileCase,
                resources,
                setResources,
                getAccounting,
                accountingData,
                pageableAccountingData,
                balance,
                getBalanceAsync,
                getProgressInfo,
                progressInfoData,
                pageableProgressInfoData,
                isLoading,
                header,
                module,
                pageableData,
                Log,
                moduleCode: code,
                attachments,
                getAttachmentsAsync,
                Storage,
                workflow,
                getWorkflowAsync,
                restartWorkflowAsync,
                startWorkflowAsync,
                startConversationAsync,
            }}
        >
            <AdvancedFiltersContext.Provider value={{ advancedFilters, setAdvancedFilters }}>
                <Header title={name} />
                <MenuBar>
                    <Switch>
                        <Redirect exact from={match.path} to={`${match.path}/list`} />
                        <Route
                            exact
                            path={`${match.path}/list`}
                            render={(props) => (
                                <List
                                    {...props}
                                    goToDetail={goToDetailHandler}
                                    goToAdd={goToAddHandler}
                                    listHead={getListHead(formName)}
                                    filterBuilder={getFiltersBuilder(formName)}
                                    formName={formName}
                                />
                            )}
                        />
                        <Route
                            path={`${match.path}/personal-info`}
                            render={(props) => (
                                <PersonalInfo
                                    {...props}
                                    onCancel={cancelHandler}
                                    goToDetail={goToDetailHandler}
                                    goToUpdate={goToUpdateHandler}
                                    formComponent={getForm(formName)}
                                />
                            )}
                        />
                        {!currentFileCase && (
                            <>
                                <Redirect exact from={`${match.path}/attachments`} to={`${match.path}/list`} />
                                <Redirect exact from={`${match.path}/accounting`} to={`${match.path}/list`} />
                                <Redirect exact from={`${match.path}/progress-info`} to={`${match.path}/list`} />
                                <Redirect exact from={`${match.path}/reports`} to={`${match.path}/list`} />
                                <Redirect exact from={`${match.path}/workflow`} to={`${match.path}/list`} />
                                {user.canChatWithClients && <Redirect exact from={`${match.path}/chat`} to={`${match.path}/list`} />}
                            </>
                        )}
                        {currentFileCase && (
                            <>
                                <Route
                                    exact
                                    path={`${match.path}/attachments`}
                                    render={(props) => <Attachments {...props} onCancel={cancelHandler} />}
                                />
                                <Route
                                    exact
                                    path={`${match.path}/accounting`}
                                    render={(props) => <Accounting {...props} onCancel={cancelHandler} />}
                                />
                                <Route
                                    exact
                                    path={`${match.path}/progress-info`}
                                    render={(props) => <ProgressInfo {...props} onCancel={cancelHandler} />}
                                />
                                <Route exact path={`${match.path}/reports`} component={Reports} />
                                <Route
                                    exact
                                    path={`${match.path}/workflow`}
                                    render={(props) => <Workflow {...props} onCancel={cancelHandler} />}
                                />
                                {user.canChatWithClients && (
                                    <Route
                                        exact
                                        path={`${match.path}/chat`}
                                        render={(props) => <Chat {...props} onCancel={cancelHandler} />}
                                    />
                                )}
                            </>
                        )}
                        <Route component={NotFound} />
                    </Switch>
                </MenuBar>
            </AdvancedFiltersContext.Provider>
        </ModulesProvider>
    );
}

Modules.propTypes = {
    dbName: PropTypes.string.isRequired,
    formName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
};

export default Modules;
